import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Switch,
    CircularProgress,
    Alert,
    Chip,
    Tooltip,
    IconButton,
    Button,
    Link,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    PauseCircle as DisableAllIcon,
    OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';

// ── Types ────────────────────────────────────────────────────────────

interface PipelineSchedule {
    schedule_id: string;
    project_id: string;
    tenant_id: string;
    name: string;
    cron_expression: string;
    cron_description?: string;
    load_mode: string;
    is_enabled: boolean;
    next_run_at: string | null;
    last_run_at: string | null;
    last_run_status: string | null;
    consecutive_failures: number;
    max_failures: number;
    connector_name?: string | null;
    connector_status?: string | null;
    project_name?: string | null;
    latest_batch_id?: string | null;
    created_at: string;
    updated_at: string;
}

// ── Helpers ──────────────────────────────────────────────────────────

const STATUS_CHIP_COLORS: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
    completed: 'success',
    failed: 'error',
    running: 'info',
    pending: 'warning',
};

function formatDate(iso: string | null): string {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

// ── Component ────────────────────────────────────────────────────────

const ScheduleManagement: React.FC = () => {
    const navigate = useNavigate();
    const [schedules, setSchedules] = useState<PipelineSchedule[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
    const [disablingAll, setDisablingAll] = useState(false);

    const fetchSchedules = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiClient.get<{ schedules: PipelineSchedule[] }>('/api/v1/schedules');
            setSchedules(response.data.schedules);
            setError(null);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to fetch schedules');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSchedules();
    }, [fetchSchedules]);

    const handleToggle = async (schedule: PipelineSchedule) => {
        const newEnabled = !schedule.is_enabled;
        setTogglingIds((prev) => new Set(prev).add(schedule.schedule_id));
        try {
            await apiClient.post(`/api/v1/schedules/${schedule.schedule_id}/toggle`, {
                is_enabled: newEnabled,
            });
            // Optimistic update
            setSchedules((prev) =>
                prev.map((s) =>
                    s.schedule_id === schedule.schedule_id ? { ...s, is_enabled: newEnabled } : s,
                ),
            );
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to toggle schedule');
        } finally {
            setTogglingIds((prev) => {
                const next = new Set(prev);
                next.delete(schedule.schedule_id);
                return next;
            });
        }
    };

    const handleDisableAll = async () => {
        const enabledSchedules = schedules.filter((s) => s.is_enabled);
        if (enabledSchedules.length === 0) return;

        setDisablingAll(true);
        try {
            await Promise.all(
                enabledSchedules.map((s) =>
                    apiClient.post(`/api/v1/schedules/${s.schedule_id}/toggle`, {
                        is_enabled: false,
                    }),
                ),
            );
            setSchedules((prev) => prev.map((s) => ({ ...s, is_enabled: false })));
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to disable all schedules');
            // Refresh to get accurate state
            fetchSchedules();
        } finally {
            setDisablingAll(false);
        }
    };

    const handleOpenBatch = (batchId: string) => {
        navigate(`/batches/${batchId}/workspace`);
    };

    const enabledCount = schedules.filter((s) => s.is_enabled).length;

    if (loading && schedules.length === 0) return <CircularProgress />;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4">Pipeline Schedules</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {schedules.length} schedule{schedules.length !== 1 ? 's' : ''} total
                        {' · '}
                        {enabledCount} enabled
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<DisableAllIcon />}
                        onClick={handleDisableAll}
                        disabled={disablingAll || enabledCount === 0}
                    >
                        {disablingAll ? 'Disabling...' : 'Disable All'}
                    </Button>
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchSchedules} disabled={loading}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Name</strong></TableCell>
                            <TableCell><strong>Project</strong></TableCell>
                            <TableCell><strong>Schedule</strong></TableCell>
                            <TableCell><strong>Load Mode</strong></TableCell>
                            <TableCell><strong>Last Run</strong></TableCell>
                            <TableCell><strong>Last Status</strong></TableCell>
                            <TableCell><strong>Next Run</strong></TableCell>
                            <TableCell><strong>Failures</strong></TableCell>
                            <TableCell><strong>Batch</strong></TableCell>
                            <TableCell align="center"><strong>Enabled</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {schedules.map((schedule) => (
                            <TableRow
                                key={schedule.schedule_id}
                                sx={{
                                    opacity: schedule.is_enabled ? 1 : 0.6,
                                    '&:hover': { opacity: 1 },
                                }}
                            >
                                <TableCell>
                                    <Typography variant="body2" fontWeight={500}>
                                        {schedule.name}
                                    </Typography>
                                    {schedule.connector_name && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {schedule.connector_name}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        {schedule.project_name || schedule.project_id?.slice(0, 8) || '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Tooltip title={schedule.cron_expression}>
                                        <Typography variant="body2">
                                            {schedule.cron_description || schedule.cron_expression}
                                        </Typography>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={schedule.load_mode}
                                        size="small"
                                        variant="outlined"
                                        sx={{ textTransform: 'capitalize' }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption">
                                        {formatDate(schedule.last_run_at)}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    {schedule.last_run_status ? (
                                        <Chip
                                            label={schedule.last_run_status}
                                            size="small"
                                            color={STATUS_CHIP_COLORS[schedule.last_run_status] || 'default'}
                                            sx={{ textTransform: 'capitalize' }}
                                        />
                                    ) : (
                                        '—'
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption">
                                        {schedule.is_enabled ? formatDate(schedule.next_run_at) : '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    {schedule.consecutive_failures > 0 ? (
                                        <Chip
                                            label={`${schedule.consecutive_failures} / ${schedule.max_failures}`}
                                            size="small"
                                            color={
                                                schedule.consecutive_failures >= schedule.max_failures
                                                    ? 'error'
                                                    : 'warning'
                                            }
                                        />
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">0</Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {schedule.latest_batch_id ? (
                                        <Tooltip title={`Open batch ${schedule.latest_batch_id.slice(0, 8)}… workspace`}>
                                            <Link
                                                component="button"
                                                variant="caption"
                                                underline="hover"
                                                onClick={() => handleOpenBatch(schedule.latest_batch_id!)}
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {schedule.latest_batch_id.slice(0, 8)}…
                                                <OpenIcon sx={{ fontSize: 12 }} />
                                            </Link>
                                        </Tooltip>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">—</Typography>
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    <Switch
                                        checked={schedule.is_enabled}
                                        onChange={() => handleToggle(schedule)}
                                        disabled={togglingIds.has(schedule.schedule_id) || disablingAll}
                                        color="primary"
                                        size="small"
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                        {schedules.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={10} align="center">
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                        No pipeline schedules found.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default ScheduleManagement;
